import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import InputGroup from '~src/domains/common/components/InputGroup';
import SelectGroup from '~src/domains/common/components/SelectGroup';
import { Page } from '~src/domains/common/enums/api';
import {
  Brand,
  InputGroupWidth,
  QuebecOption,
} from '~src/domains/common/enums/common';
import { Country, RegionsCA } from '~src/domains/common/enums/localization';
import {
  brandSelector,
  customerInfoSelector,
  isOnboardSpendSelector,
  localeSelector,
  optionsSelector,
  upcodeSelector,
} from '~src/domains/common/state/commonSelectors';
import { StartOption } from '~src/domains/start/enums/start';
import * as startFormActions from '~src/domains/start/state/startFormReducer';
import { startFormSelector } from '~src/domains/start/state/startSelectors';
import * as styles from '~src/styles/domains/start/StartPage.module.scss';
import { formatCustomerNames, getOptionsList } from '~src/utils/helpers';
import { NAMES_INPUT_ENABLED } from '~src/utils/constants';
import AddressInput from '~src/domains/start/components/AddressInput';

const getInputGroupClass = (width?: string) => {
  return width === InputGroupWidth.HALF
    ? styles['start-input-half']
    : width === InputGroupWidth.HALF_DESKTOP
    ? styles['start-input-half-desktop']
    : '';
};

const StartInputs: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const customerInfo = useSelector(customerInfoSelector);
  const options = useSelector(optionsSelector) as StartOption[];
  const locale = useSelector(localeSelector);
  const isOnBoardSpend = useSelector(isOnboardSpendSelector);
  const upc = useSelector(upcodeSelector);
  const brand = useSelector(brandSelector);

  const isUplift = brand == Brand.UPLIFT;
  const { firstName, lastName } = formatCustomerNames({
    firstName: customerInfo.first_name,
    lastName: customerInfo.last_name,
  });
  const { phone, email } = customerInfo;
  const [_, country] = locale.split('-');
  // Convert array of options into a map. (i.e ['address', 'city'] -> { address: true, city: true })
  const mappedOptions = options?.reduce(
    (prev: { [key: string]: boolean }, curr: StartOption) => {
      prev[curr] = true;
      return prev;
    },
    {},
  );

  const startForm = useSelector(startFormSelector);
  const showQuebecFields = startForm.region.value === RegionsCA.QC;
  const showSsnField = mappedOptions[StartOption.SSN] && country === Country.US;
  const showNameInputs =
    isOnBoardSpend ||
    (mappedOptions[StartOption.FIRST_NAME] &&
      mappedOptions[StartOption.LAST_NAME]);
  const inputs = t(
    isOnBoardSpend
      ? 'start:onboard-spend-application.inputs'
      : 'start:regular-application.inputs',
    {
      returnObjects: true,
    },
  ) as startFormActions.StartLocalization;
  const regions =
    t('common:regions', {
      returnObjects: true,
    }) || {};
  const incomeSources =
    (showQuebecFields && t('start:incomeSources', { returnObjects: true })) ||
    {};
  const employmentDurations =
    (showQuebecFields &&
      t('start:employmentDurations', {
        returnObjects: true,
      })) ||
    {};
  const regionOptions = getOptionsList(regions, true);
  const incomeSourceOptions = getOptionsList(incomeSources);
  const employmentDurationOptions = getOptionsList(employmentDurations);

  useEffect(() => {
    if (!showSsnField) {
      dispatch(
        startFormActions.updateInputHidden({
          name: StartOption.SSN,
          value: true,
        }),
      );
    }

    if (isOnBoardSpend) {
      dispatch(
        startFormActions.updateMultipleInputsHidden({
          inputs: [
            {
              name: StartOption.LAST_NAME,
              value: false,
            },
            {
              name: StartOption.FIRST_NAME,
              value: false,
            },
            {
              name: StartOption.EMAIL,
              value: false,
            },
            {
              name: StartOption.MOBILE,
              value: false,
            },
          ],
        }),
      );
    } else if (!showNameInputs) {
      dispatch(
        startFormActions.updateInputHidden({
          name: StartOption.FIRST_NAME,
          value: true,
        }),
      );

      dispatch(
        startFormActions.updateInputHidden({
          name: StartOption.LAST_NAME,
          value: true,
        }),
      );
    }

    if (showNameInputs) {
      dispatch(
        startFormActions.updateMultipleInputsValue({
          inputs: [
            {
              name: StartOption.FIRST_NAME,
              value: firstName,
            },
            {
              name: StartOption.LAST_NAME,
              value: lastName,
            },
          ],
        }),
      );
    }

    // We want to disable first name and last name inputs
    // for everyone except Carnival
    if (
      isOnBoardSpend ||
      (showNameInputs && upc && !NAMES_INPUT_ENABLED.includes(upc))
    ) {
      dispatch(
        startFormActions.updateMultipleInputsDisabled({
          inputs: [
            {
              name: StartOption.FIRST_NAME,
              value: true,
            },
            {
              name: StartOption.LAST_NAME,
              value: true,
            },
          ],
        }),
      );
    }
  }, [upc, firstName, lastName]);

  useEffect(() => {
    if (isOnBoardSpend) return;

    if (phone) {
      dispatch(
        startFormActions.updateInputHidden({
          name: StartOption.MOBILE,
          value: true,
        }),
      );
    }

    if (email) {
      dispatch(
        startFormActions.updateInputHidden({
          name: StartOption.EMAIL,
          value: true,
        }),
      );
    }
  }, [phone, email, isOnBoardSpend]);

  const inputGroup = (
    startForm: { [k: string]: any },
    key: string,
    optionalConfig?: {
      width?: InputGroupWidth;
      groupType?: string;
      extras?: string[];
      onChangePost?: (e: any) => void;
    },
    options?: any[],
  ) => {
    const sharedProps = {
      extras: optionalConfig?.extras || [],
      page: Page.START,
      id: key,
      input: inputs[key],
      state: startForm[key],
      reduxActions: startFormActions,
    };

    return (
      <div className={getInputGroupClass(optionalConfig?.width)}>
        {options ? (
          <SelectGroup
            {...sharedProps}
            onChangePost={optionalConfig?.onChangePost}
            options={options}
          />
        ) : (
          <InputGroup
            {...sharedProps}
            groupType={optionalConfig?.groupType || ''}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <div className={styles['start-inputs']}>
        {showNameInputs &&
          inputGroup(startForm, StartOption.FIRST_NAME, {
            width: isUplift
              ? InputGroupWidth.HALF
              : InputGroupWidth.HALF_DESKTOP,
          })}
        {showNameInputs &&
          inputGroup(startForm, StartOption.LAST_NAME, {
            width: isUplift
              ? InputGroupWidth.HALF
              : InputGroupWidth.HALF_DESKTOP,
          })}
        {isOnBoardSpend && inputGroup(startForm, StartOption.MOBILE)}
        {isOnBoardSpend && inputGroup(startForm, StartOption.EMAIL)}
        <AddressInput
          getForm={startFormSelector}
          formActions={startFormActions}
        />
        {mappedOptions[StartOption.DATE_OF_BIRTH] &&
          inputGroup(startForm, StartOption.DATE_OF_BIRTH, {
            width: InputGroupWidth.HALF_DESKTOP,
          })}
        {mappedOptions[StartOption.INCOME] &&
          inputGroup(startForm, StartOption.INCOME, {
            width: InputGroupWidth.HALF_DESKTOP,
            extras: ['currencyConfig'],
          })}
        {showSsnField &&
          inputGroup(startForm, StartOption.SSN, {
            width: InputGroupWidth.HALF_DESKTOP,
          })}
        {!isOnBoardSpend &&
          !email &&
          inputGroup(startForm, StartOption.EMAIL, {
            width: InputGroupWidth.HALF_DESKTOP,
          })}
        {!isOnBoardSpend &&
          !phone &&
          inputGroup(startForm, StartOption.MOBILE, {
            width: InputGroupWidth.HALF_DESKTOP,
          })}
        {showQuebecFields && (
          <>
            {inputGroup(startForm, QuebecOption.MONTHLY_HOUSING_COST, {
              width: InputGroupWidth.HALF_DESKTOP,
              extras: ['currencyConfig'],
            })}
            {inputGroup(
              startForm,
              QuebecOption.PRIMARY_INCOME_SOURCE,
              { width: InputGroupWidth.HALF_DESKTOP },
              incomeSourceOptions,
            )}
            {inputGroup(startForm, QuebecOption.OCCUPATION, {
              width: InputGroupWidth.HALF_DESKTOP,
            })}
            {inputGroup(
              startForm,
              QuebecOption.EMPLOYMENT_DURATION,
              { width: InputGroupWidth.HALF_DESKTOP },
              employmentDurationOptions,
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StartInputs;
